// Pure detection of members travelling together (one car, train or bus) from
// their time-aligned recent tracks, so the live map can draw them as one group
// instead of markers strung out by their phones' different upload times.
// Mirrored by Android `map/Together.kt` (same constants and cases).
import type { Schemas } from "@10twenty/api";
import type { HistoryPoint } from "../history/history";
import { mightBeMoving, type MemberRow } from "./liveCircle";
import { distanceM } from "./motion";

/** A member's latest fix must be at most this old for them to be grouped. */
export const FRESH_MS = 2 * 60_000;
/** The pairwise test looks at this much of both tracks. */
export const WINDOW_MS = 5 * 60_000;
/** A position is interpolated only between fixes at most this far either side of the instant. */
export const BRACKET_MS = 60_000;
/** The pairwise test needs at least this many time-aligned distances. */
export const MIN_SAMPLES = 3;
/** The median distance may always be this large, however sharp the fixes. */
export const NEAR_M = 75;
/** Otherwise it may be this many times the two fixes' accuracies added up. */
export const ACCURACY_FACTOR = 1.5;
/** At most one distance may exceed this (a spike the cleaning missed). */
export const SPIKE_M = 250;
/** A new pair joins only once its passing distances span this long. */
export const JOIN_MS = 2 * 60_000;
/**
 * Unless both phones report the same motion, a new pair joins only if each
 * of them went further than this over those samples (the two furthest-apart
 * fixes, less their accuracies; history's stop radius). People side by side
 * who have not gone anywhere, or someone pulling up beside a person waiting,
 * are not travelling together, even before a stop is on record.
 */
export const JOIN_MOVED_M = 100;
/** A grouped pair splits once its latest two distances both exceed this. */
export const LEAVE_M = 300;

// Outlier rejection, as the server's `cleanFixes` (server/src/locations/history.ts).
const CLEAN_WINDOW_MS = 60_000;
const COARSE_ACCURACY_M = 100;
const SHARP_ACCURACY_M = 50;
const SPIKE_FACTOR = 2;
const SPIKE_MPS = 5;

type MotionMode = Schemas["MotionMode"];

/** A member who may be grouped, with the track the test compares. */
export interface Traveller {
  userId: string;
  /** The motion reported with the latest fix, if any. */
  mode: MotionMode | null;
  /** Fixes in time order, ending with the latest, outliers left out. */
  track: HistoryPoint[];
}

/** People drawn as travelling together. */
export interface TravelGroup {
  /** The smallest member id, so the group keeps its id while its members stay. */
  id: string;
  /** In the order of the rows given to {@link travelGroups}; at least two. */
  memberIds: string[];
  /** The member with the newest fix, where the group is drawn. */
  freshestId: string;
  /** The motion every member reporting one agrees on; null when none report one or they disagree. */
  mode: MotionMode | null;
}

/** One time-aligned distance between two tracks. */
export interface PairSample {
  ts: number;
  distanceM: number;
  /** Both positions' accuracies added up. */
  accuracyM: number;
}

/**
 * Members who may be grouped: they {@link mightBeMoving}, share precisely (no
 * bubble, not paused, not outside the event), have a fix within
 * {@link FRESH_MS} and a moving trail in `tracks` (`movingTrailPoints`, keyed
 * by user id). The latest fix ends the track even if the trail lacks it;
 * fixes dated after `nowMs` are left out.
 */
export function eligibleTravellers(
  rows: readonly MemberRow[],
  tracks: ReadonlyMap<string, readonly HistoryPoint[]>,
  nowMs: number,
): Traveller[] {
  const travellers: Traveller[] = [];
  for (const row of rows) {
    const location = row.location;
    const trail = tracks.get(row.userId);
    if (!location || row.paused || row.outsideEvent || row.bubble || !trail) continue;
    if (!mightBeMoving(row, nowMs) || nowMs - Date.parse(location.ts) > FRESH_MS) continue;
    const points = notAhead(trail, nowMs).sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts));
    const last = points[points.length - 1];
    if (!last) continue;
    const locationMs = Date.parse(location.ts);
    if (locationMs > Date.parse(last.ts) && locationMs <= nowMs) {
      const { ts, lat, lon, accuracy_m, speed_mps } = location;
      points.push({ ts, lat, lon, accuracy_m, ...(speed_mps === undefined ? {} : { speed_mps }), gap_before: false });
    }
    // Cleaning may drop the latest fix (or, rarely, all of them); what is left must still be fresh.
    const track = withoutOutliers(points);
    const kept = track[track.length - 1];
    if (!kept || nowMs - Date.parse(kept.ts) > FRESH_MS) continue;
    travellers.push({ userId: row.userId, mode: row.motion?.mode ?? null, track });
  }
  return travellers;
}

/**
 * Time-aligned distances between two tracks over the last {@link WINDOW_MS}
 * up to `nowMs`: each fix of one against the other's position at that instant,
 * interpolated between its fixes within {@link BRACKET_MS} either side (never
 * across a gap). In time order, one per instant.
 */
export function pairSamples(a: Traveller, b: Traveller, nowMs: number): PairSample[] {
  const samples = new Map<number, PairSample>();
  const [trackA, trackB] = [notAhead(a.track, nowMs), notAhead(b.track, nowMs)];
  for (const [from, other] of [
    [trackA, trackB],
    [trackB, trackA],
  ] as const) {
    for (const fix of from) {
      const ts = Date.parse(fix.ts);
      if (ts < nowMs - WINDOW_MS || samples.has(ts)) continue;
      const there = positionAt(other, ts);
      if (!there) continue;
      samples.set(ts, {
        ts,
        distanceM: distanceM([fix.lon, fix.lat], [there.lon, there.lat]),
        accuracyM: fix.accuracy_m + there.accuracyM,
      });
    }
  }
  return [...samples.values()].sort((x, y) => x.ts - y.ts);
}

/**
 * The two seem to travel together: at least {@link MIN_SAMPLES} distances
 * ({@link pairSamples}), their median within {@link NEAR_M} or
 * {@link ACCURACY_FACTOR} × the (median) accuracies, at most one beyond
 * {@link SPIKE_M}, and, when both latest fixes report motion, the same mode
 * (a walker beside a slow car is no passenger).
 */
export function travellingTogether(a: Traveller, b: Traveller, nowMs: number): boolean {
  return passes(a, b, pairSamples(a, b, nowMs));
}

/**
 * Members to draw as groups, in the order of `rows`: pairs that are
 * {@link travellingTogether}, joined through each other (union-find).
 * `previous` (the last result) adds hysteresis: a pair not grouped before
 * joins only once its samples span {@link JOIN_MS} and both of them moved
 * ({@link JOIN_MOVED_M}) meanwhile; a grouped pair stays until
 * its latest two distances both exceed {@link LEAVE_M} or either member stops
 * being eligible ({@link eligibleTravellers}: a stop, pause, bubble, quiet
 * phone). Groups of one dissolve.
 */
export function travelGroups(
  rows: readonly MemberRow[],
  tracks: ReadonlyMap<string, readonly HistoryPoint[]>,
  nowMs: number,
  previous: readonly TravelGroup[] = [],
): TravelGroup[] {
  const travellers = eligibleTravellers(rows, tracks, nowMs);
  const groupedBefore = new Map<string, string>();
  for (const group of previous) for (const id of group.memberIds) groupedBefore.set(id, group.id);

  const parent = travellers.map((_, i) => i);
  const root = (i: number): number => {
    while (parent[i] !== i) i = parent[i] = parent[parent[i]!]!;
    return i;
  };
  for (let i = 0; i < travellers.length; i++) {
    for (let j = i + 1; j < travellers.length; j++) {
      const [a, b] = [travellers[i]!, travellers[j]!];
      const samples = pairSamples(a, b, nowMs);
      const before = groupedBefore.get(a.userId);
      const grouped = before !== undefined && before === groupedBefore.get(b.userId);
      if (grouped ? !split(samples) : joins(a, b, samples)) parent[root(i)] = root(j);
    }
  }

  const members = new Map<number, Traveller[]>();
  travellers.forEach((traveller, i) => {
    const r = root(i);
    members.set(r, [...(members.get(r) ?? []), traveller]);
  });
  const groups: TravelGroup[] = [];
  for (const group of members.values()) {
    if (group.length < 2) continue;
    const ids = group.map((t) => t.userId);
    const latest = (t: Traveller) => Date.parse(t.track[t.track.length - 1]!.ts);
    const freshest = group.reduce((best, t) =>
      latest(t) > latest(best) || (latest(t) === latest(best) && t.userId < best.userId) ? t : best,
    );
    const modes = new Set(group.flatMap((t) => (t.mode ? [t.mode] : [])));
    groups.push({
      id: ids.reduce((min, id) => (id < min ? id : min)),
      memberIds: ids,
      freshestId: freshest.userId,
      mode: modes.size === 1 ? [...modes][0]! : null,
    });
  }
  return groups;
}

function passes(a: Traveller, b: Traveller, samples: readonly PairSample[]): boolean {
  if (samples.length < MIN_SAMPLES) return false;
  if (a.mode && b.mode && a.mode !== b.mode) return false;
  const limit = Math.max(NEAR_M, ACCURACY_FACTOR * median(samples.map((s) => s.accuracyM)));
  if (median(samples.map((s) => s.distanceM)) > limit) return false;
  return samples.filter((s) => s.distanceM > SPIKE_M).length <= 1;
}

function joins(a: Traveller, b: Traveller, samples: readonly PairSample[]): boolean {
  if (!passes(a, b, samples)) return false;
  const [from, to] = [samples[0]!.ts, samples[samples.length - 1]!.ts];
  if (to - from < JOIN_MS) return false;
  // Activity recognition on both phones agreeing they move is evidence enough (eligible means not stationary).
  if (a.mode && a.mode === b.mode) return true;
  return Math.min(extent(a.track, from, to), extent(b.track, from, to)) > JOIN_MOVED_M;
}

/**
 * How far apart the track's two furthest fixes lie, less their accuracies,
 * from [from, to] widened by {@link BRACKET_MS} to take in the fixes the
 * samples were interpolated from. Loops and out-and-back trips count.
 */
function extent(track: readonly HistoryPoint[], from: number, to: number): number {
  const fixes = track.filter((p) => Date.parse(p.ts) >= from - BRACKET_MS && Date.parse(p.ts) <= to + BRACKET_MS);
  let most = 0;
  for (let i = 0; i < fixes.length; i++)
    for (let j = i + 1; j < fixes.length; j++) {
      const [p, q] = [fixes[i]!, fixes[j]!];
      most = Math.max(most, distanceM([p.lon, p.lat], [q.lon, q.lat]) - p.accuracy_m - q.accuracy_m);
    }
  return most;
}

function split(samples: readonly PairSample[]): boolean {
  const n = samples.length;
  return n >= 2 && samples[n - 1]!.distanceM > LEAVE_M && samples[n - 2]!.distanceM > LEAVE_M;
}

/** Fixes dated after `nowMs` (ingest accepts up to five minutes ahead) are no evidence yet. */
function notAhead(points: readonly HistoryPoint[], nowMs: number): HistoryPoint[] {
  return points.filter((p) => Date.parse(p.ts) <= nowMs);
}

function median(values: number[]): number {
  const sorted = [...values].sort((x, y) => x - y);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/** The track's position at `ts`: a fix taken then, else interpolated between fixes bracketing it. */
function positionAt(
  track: readonly HistoryPoint[],
  ts: number,
): { lat: number; lon: number; accuracyM: number } | null {
  // The first fix at or after `ts`.
  let lo = 0;
  let hi = track.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (Date.parse(track[mid]!.ts) < ts) lo = mid + 1;
    else hi = mid;
  }
  const next = track[lo];
  if (next && Date.parse(next.ts) === ts) return { lat: next.lat, lon: next.lon, accuracyM: next.accuracy_m };
  const before = track[lo - 1];
  if (!before || !next || next.gap_before) return null;
  const [t0, t1] = [Date.parse(before.ts), Date.parse(next.ts)];
  if (ts - t0 > BRACKET_MS || t1 - ts > BRACKET_MS) return null;
  const f = (ts - t0) / (t1 - t0);
  // Across the antimeridian the short way.
  let dLon = next.lon - before.lon;
  if (dLon > 180) dLon -= 360;
  else if (dLon < -180) dLon += 360;
  return {
    lat: before.lat + (next.lat - before.lat) * f,
    lon: before.lon + dLon * f,
    accuracyM: Math.max(before.accuracy_m, next.accuracy_m),
  };
}

/**
 * The track without fixes the server's `cleanFixes` would drop as outliers:
 * coarse fixes within a minute of a sharp one, and spikes (the fixes either
 * side within a minute agree it is off). History is already cleaned; this
 * catches the live fixes appended to it.
 */
export function withoutOutliers(points: readonly HistoryPoint[]): HistoryPoint[] {
  const times = points.map((p) => Date.parse(p.ts));
  const near = (i: number, j: number) =>
    j >= 0 && j < points.length && Math.abs(times[j]! - times[i]!) <= CLEAN_WINDOW_MS;
  const coarse = (i: number) => {
    if (points[i]!.accuracy_m <= COARSE_ACCURACY_M) return false;
    for (const step of [-1, 1])
      for (let j = i + step; near(i, j); j += step) if (points[j]!.accuracy_m <= SHARP_ACCURACY_M) return true;
    return false;
  };
  const spike = (i: number) => {
    let p = i - 1;
    while (p >= 0 && times[p] === times[i]) p--;
    let n = i + 1;
    while (n < points.length && times[n] === times[i]) n++;
    if (!near(i, p) || !near(i, n) || points[i]!.gap_before || points[n]!.gap_before) return false;
    const [before, fix, after] = [points[p]!, points[i]!, points[n]!];
    const seconds = (times[n]! - times[p]!) / 1000;
    const d = (x: HistoryPoint, y: HistoryPoint) => distanceM([x.lon, x.lat], [y.lon, y.lat]);
    const via = d(before, fix) + d(fix, after) - 2 * fix.accuracy_m - before.accuracy_m - after.accuracy_m;
    const direct = d(before, after) + before.accuracy_m + after.accuracy_m;
    const top = Math.max(before.speed_mps ?? 0, fix.speed_mps ?? 0, after.speed_mps ?? 0);
    return via / seconds > Math.max((SPIKE_FACTOR * direct) / seconds, top) + SPIKE_MPS;
  };
  const kept: HistoryPoint[] = [];
  let gap = false;
  points.forEach((point, i) => {
    gap ||= point.gap_before;
    if (coarse(i) || spike(i)) return;
    // A dropped fix's gap moves to the next one kept, so nothing is interpolated across it.
    kept.push(gap === point.gap_before ? point : { ...point, gap_before: gap });
    gap = false;
  });
  return kept;
}
